// EssayWritingDemo.jsx
// Essay Writing (full flow) â live HTML/CSS/JS via React + Framer Motion
// (no GIF/video).
//
// Journey (loops forever, starts once scrolled into view):
//   1. FORM   â the title types into "What's your Essay about?"; Essay Type
//               (Descriptive) and Words (500-800) are set
//   2. WRITINGâ Start Writing becomes "â¦% Writing essay, please wait!" with a
//               climbing percentage
//   3. RESULT â the essay streams into the result view with a
//               "Essay Generated Successfully!" toast and Copy / Save In Editor /
//               New Essay actions
//
// No sidebar. Fills the full width of its container.
//
// Deps: framer-motion, lucide-react   ->  npm install framer-motion
// Usage: <EssayWritingDemo />

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import useDemoInView from "./useDemoInView";
import { Sparkles, ChevronDown, Copy, Save, CirclePlus, CircleCheck } from "lucide-react";

// ---- Brand colors ----
const C = {
  blue: "#2f6df6",
  teal: "#0b8275",
  green: "#16a34a",
  ink: "#1e293b",
  slate: "#334155",
  body: "#475569",
  muted: "#5C6B80",
  hint: "#5C6B80",
  field: "#f5f6f8",
  panel: "#f7f8fa",
  borderSoft: "#eef0f4",
  border: "#e0e3ea",
  cardBorder: "#e8eaef",
  saveText: "#0f766e",
  saveBg: "#a7f3d0",
};

const TITLE = "Write an essay on the effects of global warming in Asian region";
const ESSAY = [
  "Global warming, a phenomenon characterized by the gradual increase in Earth's average surface temperature, has become a pressing issue of global concern. The Asian region, home to over half of the world's population, is particularly vulnerable due to its geographical diversity, economic dependencies, and socio-political intricacies.",
  "One of the most immediate and visible impacts of global warming in Asia is the increased frequency and severity of extreme weather events. Countries like the Philippines, Japan, and Indonesia have been experiencing more intense typhoons and cyclones, causing widespread destruction of infrastructure, loss of lives, and displacement of communities.",
  "In the Indian subcontinent, one of the most alarming consequences is the accelerated melting of the Himalayan glaciers. These glaciers are vital water sources for major river systems like the Ganges, Brahmaputra, and Indus, which sustain the agriculture and livelihoods of millions.",
  "In conclusion, the effects of global warming in the Asian region are profound and far-reaching, impacting weather patterns, water resources, agriculture, health, and biodiversity. The urgency to combat global warming grows, calling for a unified and proactive approach to safeguard the region's future.",
];

const words = (s) => s.match(/\S+\s*/g) || [s];

function Caret() {
  return (
    <motion.span
      animate={{ opacity: [1, 0] }}
      transition={{ duration: 0.55, repeat: Infinity, ease: "linear" }}
      style={{ display: "inline-block", width: 2, height: "1em", background: C.blue, verticalAlign: "text-bottom", marginLeft: 1 }}
    />
  );
}

export default function EssayWritingDemo() {
  const rootRef = useRef(null);
  const inView = useDemoInView(rootRef, { once: true, margin: "-80px" });

  const [screen, setScreen] = useState("form"); // 'form' | 'result'
  const [title, setTitle] = useState("");
  const [titleCaret, setTitleCaret] = useState(false);
  const [writing, setWriting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [startPressed, setStartPressed] = useState(false);
  const [essayParas, setEssayParas] = useState([]);
  const [essayActive, setEssayActive] = useState(-1);
  const [showToast, setShowToast] = useState(false);

  const essayRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    let alive = true;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      while (alive) {
        // reset
        setScreen("form");
        setTitle("");
        setTitleCaret(false);
        setWriting(false);
        setProgress(0);
        setStartPressed(false);
        setEssayParas([]);
        setEssayActive(-1);
        setShowToast(false);
        await sleep(900);
        if (!alive) return;

        // type title
        setTitleCaret(true);
        for (let i = 1; i <= TITLE.length; i++) {
          if (!alive) return;
          setTitle(TITLE.slice(0, i));
          await sleep(20);
        }
        setTitleCaret(false);
        await sleep(500);

        // start writing -> progress
        setStartPressed(true);
        await sleep(160);
        setStartPressed(false);
        setWriting(true);
        for (let p = 0; p <= 100; p += 5) {
          if (!alive) return;
          setProgress(p);
          await sleep(90);
        }
        await sleep(300);
        if (!alive) return;

        // result + toast
        setScreen("result");
        setShowToast(true);
        setTimeout(() => alive && setShowToast(false), 2600);

        // stream essay
        for (let pi = 0; pi < ESSAY.length; pi++) {
          if (!alive) return;
          setEssayParas((prev) => [...prev, ""]);
          setEssayActive(pi);
          const w = words(ESSAY[pi]);
          let acc = "";
          for (let wi = 0; wi < w.length; wi++) {
            if (!alive) return;
            acc += w[wi];
            const snap = acc;
            setEssayParas((prev) => {
              const n = prev.slice();
              n[pi] = snap;
              return n;
            });
            if (essayRef.current) essayRef.current.scrollTop = essayRef.current.scrollHeight;
            await sleep(14);
          }
        }
        setEssayActive(-1);
        await sleep(3200);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [inView]);

  const formVisible = screen === "form";

  return (
    <div ref={rootRef} style={styles.frame}>
      {/* FORM */}
      <div style={{ ...styles.layer, padding: "22px 34px", opacity: formVisible ? 1 : 0, pointerEvents: formVisible ? "auto" : "none" }}>
        <div style={styles.heading}>What's your Essay about?</div>

        <div style={styles.field}>
          {title.length === 0 && !titleCaret ? (
            <span style={{ fontSize: 12, color: C.muted }}>Write your Essay Title</span>
          ) : (
            <>
              <span style={{ fontSize: 12, color: C.slate }}>{title}</span>
              {titleCaret && <Caret />}
            </>
          )}
          <span style={styles.count}>{200 - title.length}/200 char</span>
        </div>

        <div style={{ ...styles.field, minHeight: 56 }}>
          <span style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
            Provide a brief description of your essay (optional). For articles requiring proper in-text
            citations and full references, please consider using the 'Literature Review' feature instead.
          </span>
          <span style={styles.count}>500/500 char</span>
        </div>

        <div style={{ display: "flex", gap: 14, marginBottom: 14 }}>
          <div style={styles.select}>
            <span style={styles.floatLabel}>Essay Type</span>
            Descriptive
            <ChevronDown size={12} color={C.muted} style={{ marginLeft: "auto" }} />
          </div>
          <div style={styles.select}>
            <span style={styles.floatLabel}>Words</span>
            500-800
            <ChevronDown size={12} color={C.muted} style={{ marginLeft: "auto" }} />
          </div>
        </div>

        <div style={styles.togglesRow}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={styles.toggle}>
              <span style={styles.toggleKnob} />
            </span>
            <span style={{ fontSize: 11, color: C.body }}>Include Headings</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={styles.checkbox} />
            <span style={{ fontSize: 11, color: C.body }}>Cited Essay</span>
          </span>
          <span style={styles.guidelinesBtn}>See Guidelines</span>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <motion.span animate={{ scale: startPressed ? 0.95 : 1 }} transition={{ duration: 0.15 }} style={styles.startBtn}>
            {writing ? (
              <>
                <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={styles.btnSpinner} />
                {progress}% Writing essay, please wait!...
              </>
            ) : (
              <>
                <Sparkles size={14} />
                Start Writing
              </>
            )}
          </motion.span>
        </div>
      </div>

      {/* RESULT */}
      <div style={{ ...styles.layer, display: "flex", flexDirection: "column", padding: "16px 22px", opacity: formVisible ? 0 : 1, pointerEvents: formVisible ? "none" : "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <span style={styles.resultHeading}>{TITLE}</span>
          <span style={{ display: "flex", gap: 8, marginLeft: 12 }}>
            <span style={styles.copyBtn}>
              <Copy size={11} />
              Copy
            </span>
            <span style={styles.saveBtn}>
              <Save size={11} />
              Save In Editor
            </span>
            <span style={styles.newBtn}>
              <CirclePlus size={11} />
              New Essay
            </span>
          </span>
        </div>
        <div ref={essayRef} style={styles.essayBox}>
          {essayParas.map((p, i) => (
            <p key={i} style={{ margin: "0 0 12px" }}>
              {p}
              {essayActive === i && <Caret />}
            </p>
          ))}
        </div>
      </div>

      {/* success toast */}
      <div style={{ ...styles.toast, opacity: showToast ? 1 : 0 }}>
        <CircleCheck size={15} color={C.green} />
        <span style={{ fontSize: 11, color: C.slate }}>Essay Generated Successfully!</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles â no sidebar, fills full container width
// ---------------------------------------------------------------------------
const styles = {
  frame: {
    position: "relative",
    width: "100%",
    height: 480,
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    border: `1px solid ${C.cardBorder}`,
    boxShadow: "0 14px 44px rgba(30,41,59,0.14)",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  layer: { position: "absolute", inset: 0, transition: "opacity 0.4s ease" },

  heading: { textAlign: "center", fontSize: 22, fontWeight: 700, color: C.ink, marginBottom: 20 },
  field: { position: "relative", background: C.field, border: `1px solid ${C.borderSoft}`, borderRadius: 10, padding: "14px 16px", minHeight: 38, marginBottom: 14 },
  count: { position: "absolute", right: 14, bottom: 8, fontSize: 9, color: C.hint },
  select: { position: "relative", flex: 1, display: "flex", alignItems: "center", background: C.field, border: `1px solid ${C.borderSoft}`, borderRadius: 10, padding: "12px 14px", fontSize: 12, color: C.slate },
  floatLabel: { position: "absolute", top: -7, left: 10, background: "#fff", padding: "0 4px", fontSize: 8, color: C.muted },
  togglesRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 20 },
  toggle: { width: 30, height: 16, borderRadius: 10, background: "#d7dce4", position: "relative", flexShrink: 0 },
  toggleKnob: { position: "absolute", left: 2, top: 2, width: 12, height: 12, borderRadius: "50%", background: "#fff" },
  checkbox: { width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${C.hint}`, flexShrink: 0 },
  guidelinesBtn: { marginLeft: "auto", fontSize: 11, color: "#fff", background: C.teal, borderRadius: 7, padding: "7px 12px" },
  startBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 7, minWidth: 170, fontSize: 13, color: "#fff", background: C.blue, borderRadius: 9, padding: "11px 26px", fontWeight: 600 },
  btnSpinner: { width: 12, height: 12, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%" },

  resultHeading: { flex: 1, fontSize: 14, fontWeight: 700, color: C.ink },
  copyBtn: { display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: C.slate, border: `1px solid ${C.border}`, borderRadius: 7, padding: "6px 11px" },
  saveBtn: { display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: C.saveText, background: C.saveBg, borderRadius: 7, padding: "6px 11px" },
  newBtn: { display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#fff", background: C.blue, borderRadius: 7, padding: "6px 11px" },
  essayBox: { flex: 1, overflow: "hidden", background: C.panel, border: `1px solid ${C.borderSoft}`, borderRadius: 10, padding: 16, fontSize: 10.5, lineHeight: 1.7, color: C.slate },

  toast: { position: "absolute", top: 14, right: 16, zIndex: 30, display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #b7f0d0", borderLeft: `4px solid ${C.green}`, borderRadius: 9, padding: "9px 14px", boxShadow: "0 8px 24px rgba(30,41,59,0.14)", transition: "opacity 0.35s ease" },
};
